import React, {
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  ReactNode,
} from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../utils';
import LabelButton from '../button/LabelButton/LabelButton';
import { LabelButtonProps } from '../button/LabelButton/LabelButton.types';

import './Tooltip.scss';

const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipPortal = TooltipPrimitive.Portal;

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = ({
  children,
  content,
  align = 'center',
  side = 'top',
  delayDuration = 100,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root> & {
  content?: ReactNode;
  align?: TooltipPrimitive.TooltipContentProps['align'];
  side?: TooltipPrimitive.TooltipContentProps['side'];
}) => {
  return (
    <TooltipPrimitive.Root delayDuration={delayDuration} {...props}>
      {!content && children}
      {content && (
        <>
          <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
          <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
              className='TooltipContent sui-p-2 sui-shadow-3 sui-rounded-[10px] sui-bg-gray-30 sui-text-white sui-caption sui-max-w-[220px] sui-z-[99999]'
              sideOffset={6}
              collisionPadding={16}
              align={align}
              side={side}
            >
              {content}
              <TooltipPrimitive.Arrow className='sui-shadow-up w-[16px] h-[8px] [fill:var(--sui-colors-gray-30)]' />
            </TooltipPrimitive.Content>
          </TooltipPrimitive.Portal>
        </>
      )}
    </TooltipPrimitive.Root>
  );
};
Tooltip.displayName = TooltipPrimitive.Root.displayName;

const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, collisionPadding = 16, children, ...props }, ref) => {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        className={cn(
          'TooltipContent sui-p-2 sui-shadow-3 sui-rounded-[10px] sui-text-neutral-text sui-caption sui-bg-neutral-background-medium sui-max-w-[440px] sui-z-[99999]',
          className
        )}
        ref={ref}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className='w-[16px] h-[8px] [fill:var(--sui-colors-gray-95)]' />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
});
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

const TooltipAction = forwardRef<
  HTMLButtonElement,
  LabelButtonProps & React.ComponentPropsWithoutRef<'button'>
>(({ children, variantType = 'tertiary', size = 'small', ...props }, ref) => {
  return (
    <LabelButton
      className='sui-block sui-mt-2 sui-mr-2 sui-ml-auto'
      ref={ref}
      variantType={variantType}
      size={size}
      {...props}
    >
      {children}
    </LabelButton>
  );
});

export default Tooltip;
export { TooltipProvider, TooltipTrigger, TooltipContent, TooltipAction, TooltipPortal };
